<template>
    <component
        :is="svgComponent"
        v-if="hasIcon"
        class="svg-icon"
        :style="svgStyle"
        :width="width"
        :height="height"
        aria-hidden="true"
        focusable="false"
    ></component>
</template>

<script setup lang="ts">
    import { computed, shallowRef, watch, type Component } from 'vue';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            iconName: string;
            width?: number;
            height?: number;
            color?: string;
        }>(),
        {
            width: 24,
            height: 24,
            color: 'inherit',
        }
    );

    const svgComponent = shallowRef<Component | null>(null);

    // ----- state -----
    /* v8 ignore next -- @preserve */
    const iconModules = import.meta.glob(
        '/src/assets/icons/**/*.svg'
    ) as Record<string, () => Promise<{ default?: Component }>>;

    // ----- computed -----
    const hasIcon = computed(() => svgComponent.value !== null);

    const svgStyle = computed(() => ({
        color: props.color,
    }));

    // ----- methods -----
    const resolveLoader = (iconName: string) => {
        const candidates = [`/src/assets/icons/${iconName}.svg`];

        for (const path of candidates) {
            const loader = iconModules[path];
            if (loader) return loader;
        }

        return null;
    };

    // ----- watch -----
    watch(
        () => props.iconName,
        async (iconName, _, onCleanup) => {
            let expired = false;

            onCleanup(() => {
                expired = true;
            });

            const loader = resolveLoader(iconName);

            if (!loader) {
                svgComponent.value = null;
                console.warn(`[gh-components] SVG icon not found: ${iconName}`);
                return;
            }

            try {
                const mod = await loader();

                if (expired) return;

                const component = mod.default;

                if (component) {
                    svgComponent.value = component;
                } else {
                    svgComponent.value = null;
                    console.warn(
                        `[gh-components] SVG icon component invalid: ${iconName}`
                    );
                }
            } catch (error) {
                if (expired) return;

                svgComponent.value = null;
                console.warn(
                    `[gh-components] SVG icon load failed: ${iconName}`,
                    error
                );
            }
        },
        { immediate: true }
    );
</script>

<style lang="scss" scoped>
    .svg-icon {
        display: block;
        max-width: 100%;
        max-height: 100%;
    }
</style>
